import React, { useEffect, useRef } from 'react';
import '../styles/MatchDaysStrip.css';
import { MdOutlineArrowBackIosNew, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

const MatchDaysStrip = ({
	allDates,
	currentDayIndex,
	setCurrentDayIndex,
	selectedDate,
	setSelectedDate,
	league
}) => {
	const { t, i18n } = useTranslation();
	const containerRef = useRef(null);
	const dayRefs = useRef([]); // <-- refs na jednotlivé dny
	const touchStartX = useRef(null); // pozice X začátku dotyku (swipe)
	const touchStartY = useRef(null); // pozice Y začátku dotyku (swipe)

	const visibleMatchDates = allDates.slice(currentDayIndex, currentDayIndex + 3);
	const today = new Date(new Date().setHours(0, 0, 0, 0));
	const tomorrow = new Date(today);
	tomorrow.setDate(tomorrow.getDate() + 1);

	const goToPreviousDay = () => {
		if (currentDayIndex > 0) {
			setCurrentDayIndex(currentDayIndex - 1);
		}
	};

	const goToNextDay = () => {
		if (currentDayIndex + 3 < allDates.length) {
			setCurrentDayIndex(currentDayIndex + 1);
		}
	};

	// Přesun na sousední hrací den (využíváno gesty na mobilu).
	// direction = +1 → další den, -1 → předchozí den.
	const selectAdjacentDate = (direction) => {
		if (!allDates.length) return;

		const currentIndex = selectedDate
			? allDates.findIndex(d => d.toDateString() === selectedDate.toDateString())
			: -1;

		const newIndex = currentIndex === -1
			? (direction > 0 ? 0 : allDates.length - 1)
			: currentIndex + direction;

		if (newIndex < 0 || newIndex >= allDates.length) return; // okraj – nic neděláme

		setSelectedDate(allDates[newIndex]);
		// Posuneme okno tak, aby vybraný den zůstal viditelný (pokud možno uprostřed).
		setCurrentDayIndex(Math.min(Math.max(newIndex - 1, 0), Math.max(allDates.length - 3, 0)));
	};

	// --- Swipe gesta (mobil): změna dne přetažením do strany ---
	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
		touchStartY.current = e.touches[0].clientY;
	};

	const handleTouchEnd = (e) => {
		if (touchStartX.current === null) return;

		const deltaX = e.changedTouches[0].clientX - touchStartX.current;
		const deltaY = e.changedTouches[0].clientY - touchStartY.current;
		const SWIPE_THRESHOLD = 40; // minimální vzdálenost pro rozpoznání swipu

		// Reagujeme jen na převážně horizontální tah (aby nekolidoval se scrollem stránky).
		if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD) {
			selectAdjacentDate(deltaX < 0 ? 1 : -1); // tah doleva → další den, doprava → předchozí
		}

		touchStartX.current = null;
		touchStartY.current = null;
	};

	const handleDateSelect = (date) => {
		const selectedIndex = allDates.findIndex(d => d.toDateString() === date.toDateString());

		if (selectedIndex === -1) return;

		const isFirst = visibleMatchDates[0]?.toDateString() === date.toDateString();
		const isLast = visibleMatchDates[visibleMatchDates.length - 1]?.toDateString() === date.toDateString();

		if (isFirst && selectedIndex > 0) {
			setCurrentDayIndex(Math.max(selectedIndex - 1, 0));
		} else if (isLast && selectedIndex < allDates.length - 1) {
			setCurrentDayIndex(Math.min(selectedIndex - 1, allDates.length - 3));
		}

		setSelectedDate(date);
	};

	// 🚀 Premium vylepšení: Scroll vybraný den doprostřed
	useEffect(() => {
		if (selectedDate && dayRefs.current.length > 0) {
			const index = visibleMatchDates.findIndex(d => d.toDateString() === selectedDate.toDateString());
			const dayElement = dayRefs.current[index];
			if (dayElement && containerRef.current) {
				const containerRect = containerRef.current.getBoundingClientRect();
				const elementRect = dayElement.getBoundingClientRect();

				const isVisible =
					elementRect.left >= containerRect.left &&
					elementRect.right <= containerRect.right;

				if (!isVisible) {
					dayElement.scrollIntoView({
						behavior: 'auto',
						inline: 'center',
						block: 'nearest',
					});
				}
			}
		}
	}, [selectedDate, visibleMatchDates]);

	const tournamentStart = league?.tournament?.startDate ? new Date(league.tournament.startDate) : null;
	const tournamentEnd = league?.tournament?.endDate ? new Date(league.tournament.endDate) : null;
	let progressPercent = null;
	if (tournamentStart && tournamentEnd && tournamentEnd.getTime() > tournamentStart.getTime()) {
		const now = Date.now();
		const ratio = (now - tournamentStart.getTime()) / (tournamentEnd.getTime() - tournamentStart.getTime());
		progressPercent = Math.max(0, Math.min(1, ratio)) * 100;
	}

	const formatProgressDate = (d) =>
		d ? `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.` : '';

	const formatProgressDateFull = (d) =>
		d ? d.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

	return (
		<div className="match-days-strip-wrapper">
		<div className="match-days-strip">
			<button
				onClick={goToPreviousDay}
				disabled={currentDayIndex === 0}
				className="strip-nav-button"
				aria-label="Previous Match Days"
			>
				<MdOutlineArrowBackIosNew />
			</button>

			<div
				className="match-days-container"
				ref={containerRef}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
			>
				{visibleMatchDates.map((date, index) => {
					const isPast = date < today;
					const isToday = date.toDateString() === today.toDateString();
					const isTomorrow = date.toDateString() === tomorrow.toDateString();
					const dayName = date.toLocaleDateString(i18n.language, { weekday: 'short' });
					const dayNumber = String(date.getDate()).padStart(2, '0');
					const monthNumber = String(date.getMonth() + 1).padStart(2, '0');
					const formattedDate = `${dayNumber}.${monthNumber}.`;

					const matchCount = league?.tournament.matches.filter(m => {
						const matchDate = new Date(m.date);
						return (
							matchDate.getFullYear() === date.getFullYear() &&
							matchDate.getMonth() === date.getMonth() &&
							matchDate.getDate() === date.getDate()
						);
					}).length || 0;

					return (
						<div
							key={index}
							ref={el => dayRefs.current[index] = el} // <-- uložíme ref pro každý den
							className={`match-day match-card-style ${isPast ? 'past-day' : ''} ${selectedDate?.toDateString() === date.toDateString() ? 'selected' : ''}`}
							onClick={() => handleDateSelect(date)}
						>
							<div className={`day-name${isToday || isTomorrow ? ' day-name-relative' : ''}`}>{isToday ? t('today') : isTomorrow ? t('tomorrow') : dayName}</div>
							<div className="day-number">{formattedDate}</div>
							<div className="match-count">
								{t(`matches.${matchCount === 1 ? 'one' : (matchCount >= 2 && matchCount <= 4 ? 'few' : 'other')}`, { count: matchCount })}
							</div>
						</div>
					);
				})}
			</div>

			<button
				onClick={goToNextDay}
				disabled={currentDayIndex + 3 >= allDates.length}
				className="strip-nav-button"
				aria-label="Next Match Days"
			>
				<MdOutlineKeyboardArrowRight />
			</button>
		</div>

		<div className="match-days-swipe-hint" aria-hidden="true">
			{t('swipeToChangeDay', { defaultValue: 'Dny přepnete přetažením do strany' })}
		</div>

		{progressPercent !== null && (
			<div
				className="tournament-progress"
				title={`${formatProgressDateFull(tournamentStart)} – ${formatProgressDateFull(tournamentEnd)}`}
				aria-label={t('tournamentProgress', { defaultValue: 'Průběh turnaje' })}
			>
				<span className="tournament-progress-date tournament-progress-date-start">
					{formatProgressDate(tournamentStart)}
				</span>
				<div className="tournament-progress-track">
					<div
						className="tournament-progress-fill"
						style={{ width: `${progressPercent}%` }}
					/>
				</div>
				<span className="tournament-progress-date tournament-progress-date-end">
					{formatProgressDate(tournamentEnd)}
				</span>
			</div>
		)}
		</div>
	);
};

export default MatchDaysStrip;
